import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

// Zone-related interfaces
export interface Zone {
  id: number;
  name: string;
  code?: string;
  iso2?: string;
  iso3?: string;
  numeric_code?: string;
  phone_code?: string;
  capital?: string;
  currency?: string;
  currency_name?: string;
  currency_symbol?: string;
  tld?: string;
  native?: string;
  region?: string;
  subregion?: string;
  latitude?: string;
  longitude?: string;
  emoji?: string;
  emojiU?: string;
  // For states
  state_code?: string;
  country_id?: number;
  country_code?: string;
  country_name?: string;
  // For cities
  state_id?: number;
  state_code_full?: string;
  state_name?: string;
}

// Commission interfaces based on new API
export interface CommissionConditions {
  package_type?: string;
  weight?: {
    min?: number;
    max?: number;
  };
}

export interface CommissionTier {
  weight_min: number;
  weight_max: number | null; // null means "and above"
  value_amount: number;
  value_type: 'fixed' | 'percentage';
}

export interface Commission {
  id: number;
  user_ids?: number[] | null;
  providers?: string[] | null;
  service_types?: string[] | null;
  origin_zones?: Array<{type: string; id: number}> | null;
  destination_zones?: Array<{type: string; id: number}> | null;
  origin_zone_details?: Array<{
    type: string;
    id: number;
    data: Zone;
  }>;
  destination_zone_details?: Array<{
    type: string;
    id: number;
    data: Zone;
  }>;
  users?: Array<{
    id: number;
    name: string;
    email: string;
    first_name: string;
    last_name: string;
  }>;
  conditions?: CommissionConditions;
  commission_tiers?: CommissionTier[];
  // Legacy fields (for backward compatibility)
  value_amount?: number;
  value_type?: 'fixed' | 'percentage';
  priority: number;
  is_active: boolean;
  created_by_admin: boolean;
  created_at: string;
  updated_at: string;
  provider_names?: string[];
  service_type_names?: string[];
}

export interface CommissionFilters {
  user_ids?: number[];
  is_general?: boolean; // for general commissions where user_ids is null
  providers?: string[];
  service_types?: string[];
  is_active?: boolean;
  page?: number;
  per_page?: number;
}

export interface CreateCommissionData {
  user_ids?: number[] | null;
  providers?: string[] | null;
  service_types?: string[] | null;
  origin_zones?: Array<{type: string; id: number}> | null;
  destination_zones?: Array<{type: string; id: number}> | null;
  conditions?: CommissionConditions;
  commission_tiers?: CommissionTier[];
  // Legacy fields (for backward compatibility)
  value_amount?: number;
  value_type?: 'fixed' | 'percentage';
  priority?: number;
  is_active?: boolean;
}

export type UpdateCommissionData = Partial<CreateCommissionData>;

export interface ApiResponse<T> {
  data: T;
  status: string;
  links?: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta?: {
    current_page: number;
    from: number | null;
    last_page: number;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    path: string;
    per_page: number;
    to: number | null;
    total: number;
  };
}

// API service functions
const getCommissions = async (filters: CommissionFilters = {}): Promise<ApiResponse<Commission[]>> => {
  const response = await api.get('/admin/commissions', { params: filters });
  return response.data;
};

const getCommission = async (id: number | string): Promise<Commission> => {
  const response = await api.get(`/admin/commissions/${id}`);
  return response.data;
};

const createCommission = async (data: CreateCommissionData): Promise<Commission> => {
  const response = await api.post('/admin/commissions', data);
  return response.data;
};

const updateCommission = async ({ id, data }: { id: number | string, data: UpdateCommissionData }): Promise<Commission> => {
  const response = await api.put(`/admin/commissions/${id}`, data);
  return response.data;
};

const deleteCommission = async (id: number | string): Promise<ApiResponse<any>> => {
  const response = await api.delete(`/admin/commissions/${id}`);
  return response.data;
};

// Custom hook for managing commissions
export const useAdminCommissions = (filters: CommissionFilters = {}) => {
  const queryClient = useQueryClient();
  
  // Query to get commissions list
  const { 
    data: response,
    isLoading,
    error,
    refetch
  } = useQuery<ApiResponse<Commission[]>>({
    queryKey: ['admin', 'commissions', filters],
    queryFn: () => getCommissions(filters),
  });
  
  // Extract data and meta
  const commissions = response?.data || [];
  const pagination = response?.meta;
  const totalCommissions = pagination?.total || 0;
  
  // Mutation to create commission
  const createCommissionMutation = useMutation({
    mutationFn: createCommission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'commissions'] });
    }
  });
  
  // Mutation to update commission
  const updateCommissionMutation = useMutation({
    mutationFn: updateCommission,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'commissions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'commission', data.id] });
    }
  });
  
  // Mutation to delete commission
  const deleteCommissionMutation = useMutation({
    mutationFn: deleteCommission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'commissions'] });
    }
  });

  return {
    commissions,
    pagination,
    totalCommissions,
    isLoading,
    error,
    refetch,
    createCommission: (data: CreateCommissionData) => 
      createCommissionMutation.mutateAsync(data),
    updateCommission: (id: number | string, data: UpdateCommissionData) => 
      updateCommissionMutation.mutateAsync({ id, data }),
    deleteCommission: (id: number | string) =>
      deleteCommissionMutation.mutateAsync(id),
    isCreating: createCommissionMutation.isPending,
    isUpdating: updateCommissionMutation.isPending,
    isDeleting: deleteCommissionMutation.isPending,
  };
};

// Custom hook to get a single commission
export const useAdminCommission = (id: number | string) => {
  const queryClient = useQueryClient();
  
  const { 
    data: commission,
    isLoading,
    error
  } = useQuery<Commission>({
    queryKey: ['admin', 'commission', id],
    queryFn: () => getCommission(id),
    enabled: !!id
  });
  
  // Mutation to update commission
  const updateCommissionMutation = useMutation({
    mutationFn: (data: UpdateCommissionData) => updateCommission({ id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'commissions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'commission', id] });
    }
  });
  
  return {
    commission,
    isLoading,
    error,
    updateCommission: (data: UpdateCommissionData) => 
      updateCommissionMutation.mutateAsync(data),
    isUpdating: updateCommissionMutation.isPending
  };
};

// Service code constants for dropdown options
export const SERVICE_CODES = [
  { value: 'express', label: 'Express Shipping' },
  { value: 'standard', label: 'Standard Shipping' },
  { value: 'economy', label: 'Economy Shipping' },
  { value: 'freight', label: 'Freight Shipping' },
  { value: 'overnight', label: 'Overnight Delivery' },
  { value: 'ground', label: 'Ground Shipping' },
];

// Zone type constants
export const ZONE_TYPES = [
  { value: 'country', label: 'Country' },
  { value: 'state', label: 'State' },
  { value: 'city', label: 'City' },
];

// Package type constants
export const PACKAGE_TYPES = [
  { value: 'standard', label: 'Standard Package' },
  { value: 'express', label: 'Express Package' },
  { value: 'document', label: 'Document' },
  { value: 'fragile', label: 'Fragile Items' },
  { value: 'heavy', label: 'Heavy Items' },
  { value: 'oversized', label: 'Oversized Package' },
];

export interface ProviderOption {
  value: string;
  label: string;
}

// Fallback while the providers endpoint loads
export const PROVIDERS: ProviderOption[] = [
  { value: 'fedex', label: 'FedEx' },
  { value: 'dhl', label: 'DHL Express' },
  { value: 'aramex', label: 'Aramex' },
  { value: 'shippingtogo', label: 'FedEx (shippingtogo)' },
];

// Backend returns { code: label }; labels can collide (ShippingToGo is
// branded FedEx), so duplicates get the code appended for the admin UI.
const toProviderOptions = (data: Record<string, string>): ProviderOption[] => {
  const entries = Object.entries(data);
  const labelCounts: Record<string, number> = {};
  entries.forEach(([, label]) => {
    labelCounts[label] = (labelCounts[label] || 0) + 1;
  });
  return entries.map(([value, label]) => ({
    value,
    label: labelCounts[label] > 1 ? `${label} (${value})` : label,
  }));
};

// Hook to fetch available providers as dropdown options
export const useProviders = () => {
  return useQuery<Record<string, string>, Error, ProviderOption[]>({
    queryKey: ['admin', 'commission-providers'],
    queryFn: async () => {
      const { data } = await api.get('/admin/commissions/providers');
      return data;
    },
    select: toProviderOptions,
  });
};

// Hook to fetch service types, merged across one or more providers
export const useServiceTypes = (providers?: string[] | string | null) => {
  const list = (Array.isArray(providers) ? providers : providers ? [providers] : []).filter(Boolean);
  return useQuery<Record<string, Record<string, string>>>({
    queryKey: ['admin', 'commission-service-types', list],
    queryFn: async () => {
      const results = await Promise.all(
        list.map((provider) =>
          api
            .get('/admin/commissions/service-types', { params: { provider } })
            .then((r) => r.data as Record<string, Record<string, string>>)
        )
      );
      const merged: Record<string, Record<string, string>> = {};
      results.forEach((data) => {
        Object.entries(data).forEach(([category, services]) => {
          merged[category] = { ...merged[category], ...services };
        });
      });
      return merged;
    },
    enabled: list.length > 0,
  });
}; 